import React, { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const words = (s) => (s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/** Searchable processing-unit picker over the active Jobber Master jobbers (name / place / PIN / GSTIN). */
export const JobberUnitPicker = ({ units, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const picked = units.find((u) => u.unit_id === value);
  const shown = useMemo(() => {
    const ws = words(q);
    if (!ws.length) return units;
    return units.filter((u) => {
      const t = `${u.unit_name} ${u.place} ${u.pincode} ${u.gstin}`.toLowerCase();
      return ws.every((w) => t.includes(w));
    });
  }, [q, units]);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="processing-unit-picker">
          <span className="truncate">{picked ? `${picked.unit_name}${picked.place ? ` — ${picked.place}` : ''}` : 'Select a jobber'}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={q} onValueChange={setQ} placeholder="Search jobber name or place..." data-testid="processing-unit-search" />
          <CommandList className="max-h-64">
            <CommandEmpty>No jobber matches.</CommandEmpty>
            <CommandGroup>
              {shown.map((u) => (
                <CommandItem key={u.unit_id} value={u.unit_id} onSelect={() => { onChange(u.unit_id); setOpen(false); setQ(''); }}
                  className="cursor-pointer" data-testid={`processing-unit-option-${u.unit_id}`}>
                  <Check className={`mr-2 h-4 w-4 ${u.unit_id === value ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="flex-1 truncate">{u.unit_name}</span>
                  {u.place && <span className="ml-2 shrink-0 text-xs text-gray-500">{u.place}{u.pincode ? ` · ${u.pincode}` : ''}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default JobberUnitPicker;
